import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDownRight, ArrowUpRight, Check, Crown, Sparkle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Counter } from '@/components/initiative/Counter';
import { type SponsorPackage } from '@/data/mock/initiative';

interface PackageCardProps {
  pkg: SponsorPackage;
  index?: number;
  inquiryHash?: string;
}

const TIER_ICONS = {
  diamond: Crown,
  gold: Star,
  silver: Sparkle,
} as const;

export function PackageCard({ pkg, index = 0, inquiryHash = '#sponsor-form' }: PackageCardProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';
  const ArrowIcon = isRTL ? ArrowDownRight : ArrowUpRight;
  const Icon = TIER_ICONS[pkg.id];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.6,
        delay: Math.min(index * 0.12, 0.36),
        ease: [0.2, 0.8, 0.2, 1],
      }}
      whileHover={{ y: -8 }}
      className={cn(
        'relative h-full group',
        pkg.featured && 'lg:-translate-y-4'
      )}
    >
      {/* Featured glow */}
      {pkg.featured && (
        <motion.div
          aria-hidden
          animate={{ opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -inset-1 rounded-[1.5rem] bg-gradient-to-br from-accent/40 via-accent/20 to-accent/40 blur-xl pointer-events-none -z-10"
        />
      )}

      {/* Featured badge */}
      {pkg.featured && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-accent text-primary font-mono-mark text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-accent/40 whitespace-nowrap">
            <Sparkle className="h-3 w-3" />
            {pkg.badge[lang]}
          </div>
        </motion.div>
      )}

      <div
        className={cn(
          'relative h-full flex flex-col p-7 lg:p-8 rounded-3xl border-2 backdrop-blur-xl transition-all duration-500 overflow-hidden',
          pkg.featured
            ? 'bg-gradient-to-br from-primary via-primary/95 to-[#1a0a5a] text-primary-foreground border-accent shadow-2xl shadow-accent/20'
            : 'bg-card/85 border-border/60 hover:border-accent/60 hover:shadow-xl hover:shadow-accent/10'
        )}
      >
        {/* Decorative corner blob */}
        <div
          aria-hidden
          className={cn(
            'absolute -top-20 -right-20 h-56 w-56 rounded-full blur-3xl pointer-events-none transition-opacity duration-500',
            pkg.featured
              ? 'bg-accent/30 opacity-100'
              : 'bg-accent/0 group-hover:bg-accent/15 opacity-0 group-hover:opacity-100'
          )}
        />

        {/* Header — index mark + icon */}
        <div className="relative flex items-center justify-between mb-6">
          <div
            className={cn(
              'font-mono-mark text-[10px] uppercase tracking-widest',
              pkg.featured ? 'text-accent' : 'text-muted-foreground'
            )}
          >
            / 0{index + 1}
          </div>
          <div
            className={cn(
              'h-11 w-11 rounded-xl flex items-center justify-center border',
              pkg.featured
                ? 'bg-accent text-primary border-accent shadow-lg shadow-accent/30'
                : 'bg-accent/10 text-accent border-accent/30'
            )}
          >
            <Icon className="h-5 w-5" strokeWidth={2.2} />
          </div>
        </div>

        {/* Tier name */}
        <h3
          className={cn(
            'font-display font-black uppercase leading-[0.9]',
            'text-4xl lg:text-5xl',
            isRTL && 'font-display-ar normal-case leading-[1.1]',
            pkg.featured ? 'text-primary-foreground' : 'text-foreground'
          )}
        >
          {pkg.name[lang]}
        </h3>

        <div
          className={cn(
            'mt-2 font-editorial-italic text-base',
            pkg.featured ? 'text-primary-foreground/70' : 'text-muted-foreground'
          )}
        >
          {pkg.badge[lang]}
        </div>

        {/* Grants display — massive numeral */}
        <div
          className={cn(
            'relative mt-7 pb-7 border-b',
            pkg.featured ? 'border-primary-foreground/20' : 'border-border/50'
          )}
        >
          <div
            className={cn(
              'font-mono-mark text-[10px] uppercase tracking-widest mb-2',
              pkg.featured ? 'text-accent' : 'text-muted-foreground'
            )}
          >
            {t('initiative.sponsor.totalGrants')}
          </div>
          <div className="flex items-baseline gap-3 leading-none">
            <span
              className={cn(
                'font-display font-black num-tabular',
                'text-7xl lg:text-[5.5rem]',
                pkg.featured
                  ? 'bg-gradient-to-b from-accent via-accent to-accent/70 bg-clip-text text-transparent drop-shadow-[0_2px_18px_rgba(255,255,255,0.18)]'
                  : 'text-foreground'
              )}
            >
              <Counter value={pkg.totalGrants} />
            </span>
            <span
              className={cn(
                'font-editorial-italic text-xl pb-1.5',
                pkg.featured ? 'text-primary-foreground/65' : 'text-foreground/60'
              )}
            >
              {t('initiative.scholarships.grants')}
            </span>
          </div>
        </div>

        {/* Benefits */}
        <div className="relative mt-7 flex-1">
          <div
            className={cn(
              'font-mono-mark text-[10px] uppercase tracking-widest mb-4',
              pkg.featured ? 'text-accent' : 'text-muted-foreground'
            )}
          >
            ※ {t('initiative.sponsor.benefitsTitle')}
          </div>
          <ul className="space-y-3">
            {pkg.benefits.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: isRTL ? 12 : -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: 0.3 + Math.min(i * 0.05, 0.3),
                }}
                className="flex gap-3 text-sm leading-snug"
              >
                <Check
                  className={cn(
                    'h-4 w-4 mt-0.5 flex-shrink-0',
                    pkg.featured ? 'text-accent' : 'text-accent'
                  )}
                  strokeWidth={3}
                />
                <span
                  className={cn(
                    pkg.featured ? 'text-primary-foreground/85' : 'text-foreground/85'
                  )}
                >
                  {b[lang]}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Footer — price + CTA */}
        <div
          className={cn(
            'relative mt-8 pt-6 border-t',
            pkg.featured ? 'border-primary-foreground/20' : 'border-border/50'
          )}
        >
          <div
            className={cn(
              'font-mono-mark text-[10px] uppercase tracking-widest',
              pkg.featured ? 'text-accent' : 'text-muted-foreground'
            )}
          >
            {t('initiative.sponsor.priceLabel')}
          </div>
          <div className="mt-1 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div
                className={cn(
                  'font-display font-black leading-none',
                  'text-2xl lg:text-3xl',
                  pkg.featured ? 'text-accent' : 'text-foreground'
                )}
              >
                {t('initiative.sponsor.priceOnRequest')}
              </div>
              <div
                className={cn(
                  'mt-1.5 text-[10px] uppercase tracking-widest font-mono-mark',
                  pkg.featured ? 'text-primary-foreground/55' : 'text-muted-foreground'
                )}
              >
                {t('initiative.sponsor.priceContactSales')}
              </div>
            </div>
          </div>

          <Link to={inquiryHash} className="block mt-5">
            <Button
              size="lg"
              className={cn(
                'w-full gap-2 font-bold uppercase tracking-wider text-sm h-12 group/btn',
                pkg.featured
                  ? 'bg-accent text-primary hover:bg-accent/90 shadow-lg shadow-accent/30'
                  : 'btn-glow'
              )}
            >
              <span>{t('initiative.sponsor.ctaInquire')}</span>
              <ArrowIcon className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
